<template>
  <v-row :style="{ padding: '10px 0', marginTop: '10px' }">
    <v-col :key="key" cols="12">
      <v-row align="baseline">
        <v-col cols="5" v-if="!flow.isTemplate">
          <v-text-field
            dense
            :label="$t('workflow.name')"
            v-model="form.name"
            outlined
            @input="$v.form.name.$touch()"
            :error-messages="errorMessages($v.form.name, 'name')"
          />
        </v-col>
        <v-col v-else>
          <v-alert
            dense
            outlined
            type="info"
            class="pa-2 my-0"
            :style="{ display: 'inline-block', marginLeft: '25%', width: '50%' }"
            v-if="canUserEdit"
          >
            <v-row dense>
              <v-col>{{ $t('workflow.templateMessage') }}</v-col>
              <v-col sm="4" md="3">
                <v-btn
                  rounded
                  small
                  outlined
                  color="blue"
                  @click="$emit('copy-template')"
                  class="button-margin"
                >
                  {{ $t('common.copy') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12" :style="{ padding: '0' }">
      <v-timeline dense>
        <div
          :draggable="!flow.isTemplate"
          @dragstart="onStartDrag(stepIndex)"
          @drop="onDrop(stepIndex)"
          @dragenter.prevent="onDragEnter(step)"
          @dragover.prevent=""
          @dragend="onDragEnd"
          v-for="(step, stepIndex) in form.workflowSteps"
          :key="stepIndex"
          :class="step.isDragged ? 'ghost' : ''"
          :style="{ marginBottom: '20px' }"
        >
          <v-timeline-item icon="mdi-drag-horizontal-variant" fill-dot>
            <v-row justify-content="baseline" :style="{ marginRight: '10px' }">
              <v-col cols="4">
                <v-combobox
                  v-model="step.name"
                  :items="stepNames"
                  :label="$t('workflow.stepName')"
                  outlined
                  :error-messages="errorMessages($v.form.workflowSteps.$each[stepIndex].name, 'name')"
                  @keyup.enter.prevent="handleCustomStepName(step)"
                  dense
                  hide-details
                >
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title><span v-html="$t('workflow.noResults')"></span></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-combobox>
              </v-col>
              <v-col cols="4">
                <v-autocomplete
                  v-model="step.calculationBase"
                  :items="calculationBasesForStep(stepIndex)"
                  outlined
                  dense
                  :label="$t('workflow.calculationBase')"
                  return-object
                  item-value="name"
                  item-text="name"
                  hide-details
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content :key="item.name">
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  :label="$t('workflow.days')"
                  v-model="step.dueInDays"
                  outlined
                  dense
                  type="number"
                  hide-details
                />
              </v-col>
              <v-col cols="1">
                <SubmitButton
                  icon="mdi-delete"
                  @click="handleDeleteStep(step, stepIndex)"
                  :disabled="flow.isTemplate || !canUserEdit"
                />
              </v-col>
              <v-col cols="6">
                <v-combobox
                  v-model="step.notifications"
                  :items="notificationsDays"
                  :label="$t('workflow.notifications')"
                  outlined
                  small-chips
                  chips
                  multiple
                  @change="handleChangeOfNotifications($event, stepIndex)"
                  hide-details
                >
                  <template v-slot:selection="data">
                    <v-chip
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      small
                      :color="color(data.item)"
                      @click="data.select"
                      @click:close="handleRemoveNotification(stepIndex, data.item)"
                    >
                      {{ data.item }}
                    </v-chip>
                  </template>
                  <template v-slot:item="data">
                    <v-list-item-content>{{ data.item }}</v-list-item-content>
                  </template>
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title><span v-html="$t('workflow.noResults')"></span></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-combobox>
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  v-model="step.fieldsToFill"
                  :items="fieldsToFill"
                  outlined
                  :label="$t('workflow.fieldsToFill')"
                  return-object
                  item-value="label"
                  item-text="label"
                  small-chips
                  chips
                  multiple
                  hide-details
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content :key="item.label">
                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-timeline-item>
        </div>
      </v-timeline>
    </v-col>
    <v-col cols="12" :style="{ marginTop: '0px' }">
      <v-row>
        <v-spacer/>
        <SubmitButton
          small
          plain
          color="error"
          @click="$emit('delete')"
          class="button-margin"
          :label="$t('common.delete')"
          :disabled="flow.used || flow.isTemplate || !canUserEdit"
        />
        <SubmitButton
          small
          outlined
          @click="handleAddNewStep"
          class="button-margin"
          :label="$t('workflow.addStep')"
          :disabled="flow.used || flow.isTemplate || !canUserEdit"
        />
        <SubmitButton
          small
          class="button-margin"
          @click="handleSaveFlow"
          :disabled="!dirty || flow.isTemplate || !canUserEdit"
        />
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import colors from 'vuetify/lib/util/colors'
import { mapState } from 'vuex'
import { addNewStep, saveStepName } from '@/services'
import { errorMessageGenerator, workflowModel } from '@/validators'
import { BASE_WORKFLOW_STEP, EMPTY_WORKFLOW_STEP, PREVIOUS_WORKFLOW_STEP } from '@/helpers'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'SingleWorkflow',
  props: {
    flow: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    const calculationBaseValues = [
      BASE_WORKFLOW_STEP,
      PREVIOUS_WORKFLOW_STEP,
      ...this.flow.workflowSteps
    ]
    return {
      form: this.prepareFlow(this.flow, calculationBaseValues),
      key: 0,
      dirty: false,
      calculationBaseValues,
      draggedItem: null,
      fieldsToFill: this.$store.state.workflowsTypes.find(i => i.type === 'payment').fieldsToFill
    }
  },
  computed: {
    ...mapState({
      stepNames: state => state.stepNames.map(i => i.name),
      notificationsDays: state => state.notificationsDays,
      canUserEdit: state => state.userPermissionsToContract.canUserEdit
    }),
    workflowSteps () {
      return this.form.workflowSteps
    }
  },
  methods: {
    calculationBasesForStep (stepIndex) {
      return this.calculationBaseValues
        .filter((i, index) => i.name && i.name.length > 0 && index < stepIndex + 2)
    },
    async handleCustomStepName (step) {
      const stepInArray = this.stepNames.find(i => i.toUpperCase() === step.name.toUpperCase())
      if (!stepInArray) {
        await saveStepName(step.name)
      }
    },
    prepareFlow (flow, calculationBaseValues) {
      const calculationBases = calculationBaseValues || this.calculationBaseValues
      const flowCopy = cloneDeep(flow)
      return {
        ...flowCopy,
        workflowSteps: flowCopy.workflowSteps.map(i => ({
          ...i,
          calculationBase: calculationBases.find(base => base.id === i.calculationBase)
        }))
      } || { workflowSteps: [] }
    },
    errorMessages (model, key) {
      return errorMessageGenerator(model, key)
    },
    async handleSaveFlow () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.$emit('update', this.form)
        this.dirty = false
        this.key = this.key + 1
      }
    },
    handleChangeOfNotifications (items, stepIndex) {
      this.form.workflowSteps[stepIndex].notifications = items.filter(i => !isNaN(i)).sort((a, b) => a - b)
    },
    handleRemoveNotification (stepIndex, item) {
      const i = this.form.workflowSteps[stepIndex].notifications.indexOf(item)
      if (i >= 0) this.form.workflowSteps[stepIndex].notifications.splice(i, 1)
    },
    color (item) {
      return Number(item) === 0 ? colors.base : Number(item) < 0 ? colors.green.lighten3 : colors.orange.lighten3
    },
    async handleAddNewStep () {
      const step = await addNewStep(
        this.form.id,
        this.$route.params.contractId,
        { ...EMPTY_WORKFLOW_STEP, order: this.form.workflowSteps.length },
        this.form.type
      )
      step.calculationBase = this.form.workflowSteps.length ? PREVIOUS_WORKFLOW_STEP : BASE_WORKFLOW_STEP
      this.form.workflowSteps.push(step)
      this.key += 1
    },
    async handleDeleteStep (step, index) {
      this.form.workflowSteps.splice(index, 1)
    },
    onDragEnter (droppedItem) {
      const draggedIndex = this.form.workflowSteps.findIndex(i => i.id === this.draggedItem.id)
      const droppedIndex = this.form.workflowSteps.findIndex(i => i.id === droppedItem.id)
      this.form.workflowSteps.splice(draggedIndex, 1)
      this.form.workflowSteps.splice(droppedIndex, 0, this.draggedItem)
      this.form.workflowSteps = this.form.workflowSteps.map((step, index) => ({ ...step, order: index }))
      this.key += 1
    },
    onDragEnd () {
      if (this.draggedItem.id || this.draggedItem.order) {
        this.form.workflowSteps = this.form.workflowSteps
          .map((step) => ({ ...step, order: step.prevOrder, isDragged: false }))
        this.key += 1
      }
    },
    onStartDrag (index) {
      this.form.workflowSteps = this.form.workflowSteps.map((step, index) => ({ ...step, prevOrder: step.order }))
      this.draggedItem = { ...this.form.workflowSteps[index] }
      this.draggedItem.isDragged = true
    },
    onDrop (index) {
      if (!this.calculationBasesForStep(index).find(i => i.id === this.draggedItem.calculationBase)) {
        this.draggedItem.calculationBase = PREVIOUS_WORKFLOW_STEP
        Object.assign(
          this.form.workflowSteps.find(i => i.id === this.draggedItem.id),
          {
            ...this.draggedItem,
            isDragged: false,
            calculationBase: PREVIOUS_WORKFLOW_STEP
          }
        )
      }
      if (this.form.workflowSteps[0].calculationBase.id === null) {
        this.form.workflowSteps[0].calculationBase = BASE_WORKFLOW_STEP
      }
      this.draggedItem = {}
      this.key += 1
    }
  },
  watch: {
    workflowSteps: {
      deep: true,
      handler (v) {
        this.calculationBaseValues = [
          BASE_WORKFLOW_STEP,
          PREVIOUS_WORKFLOW_STEP,
          ...v
        ]
      }
    },
    form: {
      deep: true,
      handler () {
        this.dirty = true
      }
    },
    dirty (v) {
      this.$eventBus.$emit(v ? 'SET_DIRTY' : 'UNSET_DIRTY')
    }
  },
  validations: workflowModel
}
</script>

<style>
.button-margin {
  margin: 10px
}
.ghost {
  opacity: 0.5;
}
</style>
